<template>
  <div class="aep-root">
    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>

    <!-- Main content -->
    <div class="aep-main">
      <!-- Header -->
      <div class="aep-header">
        <h2 class="aep-title">All entry points</h2>
        <button class="aep-new-btn" @click="$emit('tab-change', 'create-new')">
          <i class="fa fa-plus"></i> New entry point
        </button>
      </div>

      <!-- Body: table -->
      <div class="aep-body">
        <div class="aep-table-wrap">
          <vue-good-table
            :key="tableKey"
            mode="remote"
            styleClass="vgt-table good-table condensed striped"
            :columns="columns"
            :rows="rows"
            :totalRows="total"
            :pagination-options="{
              enabled: true,
              perPage: pageSize,
              setCurrentPage: page
            }"
            :search-options="{ enabled: false }"
            :row-style-class="rowStyleClass"
            @on-page-change="onPageChange"
            @on-per-page-change="onPerPageChange"
            @on-column-filter="onColumnFilter"
          >
            <div slot="emptystate"><nv-empty-state icon="fa-map-marker" title="No entry points found" hint="Try changing the filters, or create one from the Create new tab." /></div>
            <template slot="table-row" slot-scope="props">

              <!-- KEYWORD -->
              <span v-if="props.column.field === 'keyword'">
                <strong class="aep-keyword-text">{{ props.row.keyword }}</strong>
              </span>

              <!-- PHRASES -->
              <span v-else-if="props.column.field === 'phrases'">
                <template v-if="getPhrases(props.row).length">
                  <span
                    v-for="ph in getPhrases(props.row)"
                    :key="ph"
                    class="aep-phrase-badge"
                  >{{ ph }}</span>
                </template>
                <span v-else>—</span>
              </span>

              <!-- CHANNELS -->
              <span v-else-if="props.column.field === 'channels'">
                <span
                  v-for="ch in getChannels(props.row)"
                  :key="ch"
                  class="aep-ch-badge"
                  :class="`aep-ch-${ch}`"
                >{{ chLabel(ch) }}</span>
              </span>

              <!-- JOURNEY / STAGE -->
              <span v-else-if="props.column.field === 'journey'" class="aep-journey-text">
                <template v-if="props.row.journey || getJumpStage(props.row)">
                  <span v-if="props.row.journey">{{ props.row.journey }}</span>
                  <template v-if="props.row.journey && getJumpStage(props.row)"> → </template>
                  <span v-if="getJumpStage(props.row)">{{ getJumpStage(props.row) }}</span>
                </template>
                <span v-else>—</span>
              </span>

              <!-- CREATED -->
              <span v-else-if="props.column.field === 'created_at'">
                {{ formatDate(props.row.created_at) }}
              </span>

              <!-- CREATED BY -->
              <span v-else-if="props.column.field === 'created_by'">
                {{ formatEmail(props.row.created_by) }}
              </span>

              <!-- VIEW OUTPUTS / EDIT -->
              <span v-else-if="props.column.field === 'actions'">
                <button
                  v-if="props.row.entry_point_id"
                  class="aep-view-btn"
                  @click="openOutputs(props.row)"
                >View outputs</button>
                <router-link
                  v-else-if="props.row.keyword_id"
                  :to="{ path: '/NewKeywordsDetails', query: { keywordId: props.row.keyword_id } }"
                  class="aep-edit-btn"
                >Edit</router-link>
              </span>

              <span v-else class="aep-cell-text">{{ props.formattedRow[props.column.field] || '—' }}</span>

            </template>
          </vue-good-table>
        </div>
      </div>
    </div>

    <!-- Outputs sidebar — full height alongside main content -->
    <transition name="aep-slide">
      <div v-if="selectedId" class="aep-sidebar">
        <div class="aep-sidebar-header">
          <span class="aep-sidebar-title">Outputs <strong>{{ selectedKeyword }}</strong></span>
          <button class="aep-sidebar-close" @click="closeSidebar"><i class="fa fa-times"></i></button>
        </div>
        <div v-if="sidebarLoading" class="aep-sidebar-loading">
          <i class="fa fa-spinner fa-spin"></i> Loading…
        </div>
        <output-sidebar v-else :output="entryPointOutput" :show-ready-header="false" @output_copied="onDashboardOutputCopied" @output_downloaded="onDashboardQrDownloaded" />
      </div>
    </transition>

  </div>
</template>

<script>
import api from '../../../../utils/api'
import OutputSidebar from '../create-new-entry-point/output-sidebar/OutputSidebar.vue'
import trackEvent from '../../../../utils/trackEvent'

const CH_LABELS = { whatsapp: 'WA', messenger: 'MS', line: 'LINE' }

export default {
  name: 'AllEntryPoints',
  components: { OutputSidebar },

  data () {
    return {
      loading: true,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      tableKey: 0,
      filterKeyword: '',
      filterPhrase: '',
      filterClient: '',
      filterJourney: '',
      filterCountry: '',
      filterDateFrom: '',
      filterDateTo: '',
      clientOptions: [],
      journeyOptions: [],
      countryOptions: [],

      selectedId: null,
      selectedKeyword: '',
      sidebarLoading: false,
      entryPointOutput: null,
      _panelOpenedAt: null,
      _pendingSearchTerm: null,
      _searchDebounceTimer: null
    }
  },

  computed: {
    columns () {
      return [
        {
          label: 'Keyword',
          field: 'keyword',
          sortable: false,
          width: '120px',
          filterOptions: { enabled: true, placeholder: 'Search keyword…' }
        },
        {
          label: 'Phrase(s)',
          field: 'phrases',
          sortable: false,
          width: '160px',
          filterOptions: { enabled: true, placeholder: 'Search phrase…' }
        },
        {
          label: 'Channels',
          field: 'channels',
          sortable: false,
          width: '90px'
        },
        {
          label: 'Client',
          field: 'client',
          sortable: false,
          width: '120px',
          filterOptions: {
            enabled: true,
            placeholder: 'All clients',
            filterDropdownItems: this.clientOptions
          }
        },
        {
          label: 'Journey / Stage',
          field: 'journey',
          sortable: false,
          width: '180px',
          filterOptions: {
            enabled: true,
            placeholder: 'All journeys',
            filterDropdownItems: this.journeyOptions
          }
        },
        {
          label: 'Country',
          field: 'country',
          sortable: false,
          width: '100px',
          filterOptions: {
            enabled: true,
            placeholder: 'All countries',
            filterDropdownItems: this.countryOptions
          }
        },
        {
          label: 'Created',
          field: 'created_at',
          sortable: false,
          width: '110px',
          filterOptions: { enabled: true, placeholder: 'YYYY-MM-DD' }
        },
        {
          label: 'Created by',
          field: 'created_by',
          sortable: false,
          width: '110px'
        },
        {
          label: '',
          field: 'actions',
          sortable: false,
          width: '100px'
        }
      ]
    }
  },

  methods: {
    rowStyleClass (row) {
      return this.selectedId && row.keyword_id === this.selectedId ? 'aep-row--selected' : ''
    },

    chLabel (ch) { return CH_LABELS[ch] || ch.toUpperCase() },

    formatEmail (email) {
      if (!email) return '—'
      const parts = email.split('@')[0].split('.')
      return parts.map((p, i) => i === 0
        ? p.charAt(0).toUpperCase() + p.slice(1)
        : p.charAt(0).toUpperCase()
      ).join(' ')
    },

    getPhrases (row) {
      return row.phrases ? row.phrases.split('|||') : []
    },

    getChannels (row) {
      try {
        const ad = typeof row.additional_details === 'string'
          ? JSON.parse(row.additional_details)
          : (row.additional_details || {})
        return (ad.interface || []).map(i => i.toLowerCase())
      } catch (e) { return [] }
    },

    getJumpStage (row) {
      try {
        const ad = typeof row.additional_details === 'string'
          ? JSON.parse(row.additional_details)
          : (row.additional_details || {})
        return ad.jumpState || null
      } catch (e) { return null }
    },

    formatDate (iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },

    async loadFilterOptions () {
      try {
        const { data } = await api.buildRequest('entry-points/filter-options', 'GET')
        const payload = data.payload || data
        this.clientOptions = (payload.clients || []).sort()
        this.journeyOptions = (payload.journeys || []).sort()
        this.countryOptions = (payload.countries || []).sort()
        this.tableKey++
      } catch (err) {
        console.error('[AllEntryPoints] filter options failed', err)
      }
    },

    async loadRows () {
      this.loading = true
      try {
        const params = { page: this.page, limit: this.pageSize }
        if (this.filterKeyword) params.keyword = this.filterKeyword
        if (this.filterPhrase) params.phrase = this.filterPhrase
        if (this.filterClient) params.client = this.filterClient
        if (this.filterJourney) params.journey = this.filterJourney
        if (this.filterCountry) params.country = this.filterCountry
        if (this.filterDateFrom) params.date_from = this.filterDateFrom
        if (this.filterDateTo) params.date_to = this.filterDateTo
        const { data } = await api.buildRequest('entry-points/list', 'GET', params)
        const payload = data.payload || data
        this.rows = payload.rows || []
        this.total = payload.total || 0
        if (this._pendingSearchTerm !== null) {
          trackEvent({ event_name: 'dashboard_search_performed', properties: { search_term: this._pendingSearchTerm, results_count: this.total } })
          this._pendingSearchTerm = null
        }
      } catch (err) {
        console.error('[AllEntryPoints] load failed', err)
      } finally {
        this.loading = false
      }
    },

    onPageChange (params) {
      this.page = params.currentPage
      this.loadRows()
    },

    onPerPageChange (params) {
      this.pageSize = params.currentPerPage
      this.page = 1
      this.loadRows()
    },

    onColumnFilter (params) {
      const f = params.columnFilters || {}
      const prev = { keyword: this.filterKeyword, phrase: this.filterPhrase, client: this.filterClient, journey: this.filterJourney, country: this.filterCountry, date_from: this.filterDateFrom }

      this.filterKeyword = f.keyword || ''
      this.filterPhrase = f.phrases || ''
      this.filterClient = f.client || ''
      this.filterJourney = f.journey || ''
      this.filterCountry = f.country || ''
      this.filterDateFrom = f.created_at || ''
      this.filterDateTo = ''
      this.page = 1

      // Fire per-filter events for each changed field
      const filterFields = [
        { vgtKey: 'keyword', stateKey: 'keyword', filterType: 'keyword', isSearch: true },
        { vgtKey: 'phrase', stateKey: 'phrase', filterType: 'phrase', isSearch: true },
        { vgtKey: 'client', stateKey: 'client', filterType: 'project', isSearch: false },
        { vgtKey: 'journey', stateKey: 'journey', filterType: 'journey', isSearch: false },
        { vgtKey: 'country', stateKey: 'country', filterType: 'country', isSearch: false },
        { vgtKey: 'date_from', stateKey: 'date_from', filterType: 'date', isSearch: false }
      ]
      let hasSearchChange = false
      filterFields.forEach(({ stateKey, filterType, isSearch }) => {
        const newVal = this['filter' + stateKey.charAt(0).toUpperCase() + stateKey.slice(1).replace('_from', 'From').replace('_', '')] || ''
        const oldVal = prev[stateKey] || ''
        if (newVal === oldVal) return
        if (newVal) {
          if (isSearch) {
            hasSearchChange = true
          } else {
            trackEvent({ event_name: 'dashboard_filter_applied', properties: { filter_type: filterType, filter_value: newVal } })
          }
        } else {
          trackEvent({ event_name: isSearch ? 'dashboard_search_cleared' : 'dashboard_filter_cleared', properties: { filter_type: filterType } })
        }
      })

      if (hasSearchChange) {
        const term = this.filterKeyword || this.filterPhrase
        clearTimeout(this._searchDebounceTimer)
        this._searchDebounceTimer = setTimeout(() => {
          this._searchDebounceTimer = null
          this._pendingSearchTerm = term
          this.loadRows()
        }, 400)
      } else {
        this.loadRows()
      }
    },

    async openOutputs (row) {
      this._panelOpenedAt = Date.now()
      trackEvent({ event_name: 'dashboard_outputs_panel_opened', properties: { keyword_key: row.keyword, entry_point_id: row.entry_point_id, channels: this.getChannels(row) } })
      this.selectedId = row.keyword_id
      this.selectedKeyword = row.keyword
      this.sidebarLoading = true
      this.entryPointOutput = null
      try {
        const [kwRes, phraseRes] = await Promise.all([
          api.buildRequest('/getKeywordDetails', 'POST', {
            selector: { id: row.keyword_id },
            sort: { createdAt: -1 },
            limit: 20,
            skip: 0
          }),
          api.buildRequest('getPhraseList', 'POST', {
            selector: { keyword_id: row.keyword_id },
            sort: { createdAt: -1 }
          })
        ])
        const kd = kwRes.data.payload.keywordDetails
        const keyword = { ...kd.data, id: kd.id }
        const phrase = phraseRes.data.payload.phrases || []

        const phraseIds = phrase.map(p => p.id).filter(Boolean)
        let existingLinks = []
        if (phraseIds.length) {
          const linksRes = await api.buildRequest('/getEntryPointLinks', 'POST', { context_ids: phraseIds })
          existingLinks = linksRes.data.payload || []
        }

        this.entryPointOutput = { keyword, phrase, existingLinks, entry_point_id: row.entry_point_id }
      } catch (err) {
        console.error('[AllEntryPoints] openOutputs failed', err)
      } finally {
        this.sidebarLoading = false
      }
    },

    closeSidebar () {
      const timeOpen = this._panelOpenedAt ? Math.round((Date.now() - this._panelOpenedAt) / 1000) : null
      trackEvent({ event_name: 'dashboard_outputs_panel_closed', properties: { keyword_key: this.selectedKeyword, time_open_seconds: timeOpen } })
      this._panelOpenedAt = null
      this.selectedId = null
      this.entryPointOutput = null
    },

    onDashboardOutputCopied (payload) {
      trackEvent({ event_name: 'dashboard_output_copied', properties: { keyword_key: this.selectedKeyword, field: payload.output_type, channel: payload.channel || null, entry_point_id: payload.entry_point_id } })
    },

    onDashboardQrDownloaded (payload) {
      trackEvent({ event_name: 'dashboard_qr_downloaded', properties: { keyword_key: this.selectedKeyword, entry_point_id: payload.entry_point_id, channel: payload.channel || null } })
    }
  },

  mounted () {
    this.loadFilterOptions()
    this.loadRows()
  }
}
</script>
<style scoped src="./AllEntryPoints.css"></style>
<style>
.aep-table-wrap .vgt-table {
  table-layout: fixed !important;
  width: 100% !important;
}
.aep-table-wrap .vgt-table th,
.aep-table-wrap .vgt-table td {
  text-align: left !important;
  white-space: normal !important;
  word-break: break-all !important;
  overflow-wrap: anywhere !important;
}
.aep-table-wrap .aep-row--selected td {
  background-color: #e6f4f4 !important;
}
</style>



// WEBPACK FOOTER //
// AllEntryPoints.vue?606b46f8